import React, { Component } from "react";
import { connect } from "react-redux";
import { Row, Col, message, Select, Upload, Tooltip, Switch } from "antd";
import { PictureOutlined, InboxOutlined, PlusOutlined, InfoCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { FormItem, InputItem, InputNumberItem, SelectItem, TextAreaItem, DatePickerItem, ButtonItem, DynamicCardForm } from "../../../../Components/Items";
import SelectMarcasItem from "../../../../Components/SelectMarcasItem";
import SelectClientesItem from "../../../../Components/SelectClientesItem";
import ModalMarca from "../../Marcas/ModalMarca/ModalMarca";
import ModalFormCMMS from "../componentes/ModalFormCMMS";
import SelectorEstatusEquipo from "../componentes/SelectorEstatusEquipo";
import CampoSugerenciasCMMS from "../componentes/CampoSugerenciasCMMS";
import { IconActivos } from "../../../../Icons/Icons";
import { cmmsPost, normFile, fileListAPayload, cargarSucursales } from "../cmmsComun";

const { Option } = Select;

const CAMPOS_FECHA = ["fechaInstalacion", "inicioGarantia", "finGarantia", "fechaVenta"];

const subirSinEnviar = (limiteMb) => (file) => {
    if (file.size / 1024 / 1024 >= limiteMb) {
        message.error(`El archivo debe pesar menos de ${limiteMb}MB`);
        return Upload.LIST_IGNORE;
    }
    return false; // se manda en base64 al guardar
};

// Alta / edicion de un Activo CMMS.
// Modelo, ubicacion y area son texto libre con sugerencias de lo ya capturado.
// En edicion el cliente y la ubicacion son de solo lectura: se cambian con
// "Transferir" (en la ficha) para que quede historial.
class ModalActivo extends Component {
    formRef = React.createRef();

    state = {
        loading: false,
        tipos: [],
        sucursales: [],
        // Listas de opciones (Configuracion CMMS)
        categorias: [],
        pertenencias: [],
        jerarquias: [],
        rutinas: [],
        // Espejo de los campos que filtran las sugerencias y habilitan la cascada
        tipoEquipoId: null,
        marcaId: null,
        clienteId: null,
        direccionId: null,
        ubicacion: "",
        sinSerie: false,
        modalMarca: false,
        llaveMarcas: 0,
        ubicacionActualTexto: "",
    };

    componentDidUpdate(prevProps) {
        if (this.props.visible && !prevProps.visible) this.inicializar();
    }

    inicializar = async () => {
        this.formRef.current?.resetFields();
        this.setState({
            loading: true,
            sucursales: [],
            tipoEquipoId: null,
            marcaId: null,
            clienteId: null,
            direccionId: null,
            ubicacion: "",
            sinSerie: false,
            ubicacionActualTexto: "",
        });
        try {
            const [resTipos, resConfig, resRutinas] = await Promise.all([
                cmmsPost(this.props, "selectTiposEquipo", { valor: 1 }),
                cmmsPost(this.props, "datosConfiguracionCMMS"),
                cmmsPost(this.props, "selectRutinasCMMS", { valor: 1 }),
            ]);
            const config = resConfig.data.success === 2 ? resConfig.data.configuracion : {};
            this.setState({
                tipos: resTipos.data.success === 2 ? resTipos.data.tipos : [],
                categorias: config.categorias || [],
                pertenencias: config.pertenencias || [],
                jerarquias: config.jerarquias || [],
                rutinas: resRutinas.data.success === 2 ? resRutinas.data.rutinas : [],
            });
            if (this.props.modo === "editar" && this.props.activoId) await this.consultarActivo();
        } catch (error) {
            console.log(error);
        } finally {
            this.setState({ loading: false });
        }
    };

    consultarActivo = async () => {
        const res = await cmmsPost(this.props, "datosActivoCMMS", { id: this.props.activoId });
        if (res.data.success !== 2) return message.error(res.data.message || "No se encontró el activo");
        const a = res.data.activo;
        const valores = { ...a, marcaId: a.marcaId || undefined, rutinaId: a.rutinaId || undefined };
        CAMPOS_FECHA.forEach((c) => (valores[c] = a[c] ? moment(a[c]) : undefined));
        this.formRef.current?.setFieldsValue(valores);
        this.setState({ tipoEquipoId: a.tipoEquipoId, marcaId: a.marcaId || null, sinSerie: !!a.sinSerie, clienteId: a.clienteId || null });

        if (a.clienteId) {
            const sucursales = await cargarSucursales(this.props, a.clienteId);
            const sucursal = sucursales.find((s) => String(s._id) === String(a.direccionId));
            this.setState({ ubicacionActualTexto: [sucursal?.nombre, a.ubicacion, a.area].filter(Boolean).join(" · ") });
        }
    };

    // Opciones de una lista de Configuracion. Si el activo trae un valor que ya no
    // esta en la lista (se renombro o se quito), se agrega para no perderlo al editar.
    opciones = (lista, campo) => {
        const actual = this.formRef.current?.getFieldValue(campo);
        const valores = actual && !lista.includes(actual) ? [...lista, actual] : lista;
        return valores.map((v) => (
            <Option key={v} value={v}>
                {v}
            </Option>
        ));
    };

    onChangeCliente = async (clienteId) => {
        this.formRef.current?.setFieldsValue({ direccionId: undefined, ubicacion: undefined, area: undefined });
        this.setState({ clienteId: clienteId || null, direccionId: null, ubicacion: "", sucursales: [] });
        if (clienteId) this.setState({ sucursales: await cargarSucursales(this.props, clienteId) });
    };

    // Mantiene el espejo de los campos que filtran las sugerencias.
    onValuesChange = (cambios) => {
        const espejo = {};
        if ("tipoEquipoId" in cambios) espejo.tipoEquipoId = cambios.tipoEquipoId || null;
        if ("marcaId" in cambios) espejo.marcaId = cambios.marcaId || null;
        if ("direccionId" in cambios) espejo.direccionId = cambios.direccionId || null;
        if ("ubicacion" in cambios) espejo.ubicacion = cambios.ubicacion || "";
        if (Object.keys(espejo).length) this.setState(espejo);
    };

    onFinish = async (values) => {
        this.setState({ loading: true });
        try {
            const [fotos, archivos] = await Promise.all([fileListAPayload(values.fotos), fileListAPayload(values.archivos)]);
            const payload = { ...values, sinSerie: this.state.sinSerie, id: this.props.activoId, fotos, archivos };
            CAMPOS_FECHA.forEach((c) => (payload[c] = values[c] ? moment(values[c]).format("YYYY-MM-DD") : null));
            ["marcaId", "clienteId", "direccionId", "rutinaId"].forEach((c) => (payload[c] = values[c] || null));

            const ruta = this.props.modo === "editar" ? "editarActivoCMMS" : "agregarActivoCMMS";
            const res = await cmmsPost(this.props, ruta, payload);
            if (res.data.success === 2) {
                message.success(this.props.modo === "editar" ? "Activo actualizado" : `Activo registrado con folio ${res.data.folio}`);
                this.props.onClose(res.data.codigo);
            } else {
                message.error(res.data.message || "No se pudo guardar el activo");
            }
        } catch (error) {
            console.log(error);
            message.error("No se pudo guardar el activo");
        } finally {
            this.setState({ loading: false });
        }
    };

    renderEquipo() {
        return (
            <DynamicCardForm title="Equipo" xs={24} lg={14}>
                <Row gutter={12}>
                    <Col xs={24} md={10}>
                        <FormItem label="Categoría" name="categoria" required tooltip="Clasificación general del equipo. Las opciones se editan en Mantenimiento > Configuración.">
                            <SelectItem placeholder="Categoría">
                                {this.opciones(this.state.categorias, "categoria")}
                            </SelectItem>
                        </FormItem>
                    </Col>
                    <Col xs={24} md={14}>
                        <FormItem
                            label="Tipo de Activo"
                            name="tipoEquipoId"
                            required
                            tooltip="Tipo de equipo (Ventilador, Desfibrilador...). De él hereda la periodicidad de preventivo y, más adelante, su rutina."
                        >
                            <SelectItem placeholder="Elija el tipo de equipo">
                                {this.state.tipos.map((t) => (
                                    <Option key={t._id} value={t._id}>
                                        {t.nombre}
                                    </Option>
                                ))}
                            </SelectItem>
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col xs={24} md={12}>
                        <FormItem label="Marca" required tooltip="Marca del fabricante. Si no existe, agréguela con el botón +.">
                            <div style={{ display: "flex", gap: 6 }}>
                                <FormItem name="marcaId" noStyle rules={[{ required: true, message: "Falta el campo: Marca" }]}>
                                    <SelectMarcasItem key={this.state.llaveMarcas} empresaId={this.props.empresaId} headersToken={this.props.headersToken} width="100%" placeholder="Marca" />
                                </FormItem>
                                <Tooltip title="Agregar marca">
                                    <ButtonItem type="default" htmlType="button" icon={<PlusOutlined />} onClick={() => this.setState({ modalMarca: true })} />
                                </Tooltip>
                            </div>
                        </FormItem>
                    </Col>
                    <Col xs={24} md={12}>
                        <FormItem label="Modelo" name="modelo" required max={120} tooltip="Escriba el modelo o elija uno de los ya capturados para este tipo y marca.">
                            <CampoSugerenciasCMMS
                                empresaId={this.props.empresaId}
                                headersToken={this.props.headersToken}
                                placeholder="Modelo"
                                params={{ campo: "modelo", tipoEquipoId: this.state.tipoEquipoId, marcaId: this.state.marcaId }}
                            />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col xs={24} md={12}>
                        <FormItem
                            label="No. de Serie"
                            name="numeroSerie"
                            required={!this.state.sinSerie}
                            max={80}
                            tooltip="Número de serie del fabricante. Si el equipo no tiene, active el interruptor de la derecha."
                        >
                            <InputItem placeholder={this.state.sinSerie ? "El equipo no tiene serie" : "No. de serie"} disabled={this.state.sinSerie} />
                        </FormItem>
                    </Col>
                    <Col xs={24} md={12}>
                        <FormItem label=" " colon={false}>
                            <div className="cmms-switch-linea">
                                <Switch
                                    size="small"
                                    checked={this.state.sinSerie}
                                    onChange={(sinSerie) => {
                                        this.setState({ sinSerie }, () => this.formRef.current?.validateFields(["numeroSerie"]).catch(() => {}));
                                        if (sinSerie) this.formRef.current?.setFieldsValue({ numeroSerie: "" });
                                    }}
                                />
                                El equipo no tiene número de serie
                            </div>
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col xs={24} md={8}>
                        <FormItem label="No. de Inventario" name="numeroInventario" max={60} tooltip="Número de inventario o patrimonial que el cliente le asignó.">
                            <InputItem placeholder="Inventario" />
                        </FormItem>
                    </Col>
                    <Col xs={24} md={8}>
                        <FormItem label="ID Externo" name="idExterno" max={60} tooltip="ID del equipo en el sistema del cliente (ej. ID TINC).">
                            <InputItem placeholder="ID del cliente" />
                        </FormItem>
                    </Col>
                    <Col xs={24} md={8}>
                        <FormItem label="ID Interno" name="idInterno" max={60} tooltip="Identificador propio opcional. El folio del activo se asigna solo al guardar.">
                            <InputItem placeholder="ID interno" />
                        </FormItem>
                    </Col>
                </Row>
                <FormItem
                    label="Rutina propia de preventivo (opcional)"
                    name="rutinaId"
                    tooltip="Normalmente el equipo usa la rutina de su Tipo de Activo. Elija una aquí sólo si este equipo en particular lleva otra rutina de preventivo."
                >
                    <SelectItem placeholder="Usar la rutina del tipo de activo" allowClear>
                        {this.state.rutinas.map((r) => (
                            <Option key={r._id} value={r._id}>
                                {[r.codigo, r.titulo].filter(Boolean).join(" - ")}
                            </Option>
                        ))}
                    </SelectItem>
                </FormItem>
            </DynamicCardForm>
        );
    }

    renderEstado() {
        return (
            <DynamicCardForm title="Estado y fechas" xs={24} lg={10}>
                <FormItem label="Estatus" name="estatusEquipo" required initialValue={1} tooltip="Estado actual del equipo. Al concluir un servicio se actualiza con la condición de entrega.">
                    <SelectorEstatusEquipo />
                </FormItem>
                <Row gutter={12}>
                    <Col xs={12}>
                        <FormItem label="Pertenencia" name="pertenencia" required tooltip="De quién es el equipo (del cliente, arrendado, en comodato...). Las opciones se editan en Configuración.">
                            <SelectItem placeholder="Pertenencia">{this.opciones(this.state.pertenencias, "pertenencia")}</SelectItem>
                        </FormItem>
                    </Col>
                    <Col xs={12}>
                        <FormItem label="Jerarquía" name="jerarquia" initialValue="Por definir" tooltip="Qué tan crítico es el equipo. Sirve para priorizar su atención. Las opciones se editan en Configuración.">
                            <SelectItem placeholder="Jerarquía">{this.opciones(this.state.jerarquias, "jerarquia")}</SelectItem>
                        </FormItem>
                    </Col>
                    <Col xs={24}>
                        <FormItem
                            label="Tiempo máximo de respuesta (horas)"
                            name="tiempoMaximoRespuestaHrs"
                            numberCero
                            tooltip="En cuántas horas como máximo se debe atender una falla de este equipo. Se usará en los tickets para avisar cuando se rebase."
                        >
                            <InputNumberItem placeholder="Ej. 24" min={0} />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col xs={12}>
                        <FormItem label="Instalación" name="fechaInstalacion">
                            <DatePickerItem placeholder="Fecha" style={{ width: "100%" }} format="DD/MM/YYYY" />
                        </FormItem>
                    </Col>
                    <Col xs={12}>
                        <FormItem label="Venta" name="fechaVenta" tooltip="Si la empresa le vendió el equipo al cliente.">
                            <DatePickerItem placeholder="Fecha" style={{ width: "100%" }} format="DD/MM/YYYY" />
                        </FormItem>
                    </Col>
                    <Col xs={12}>
                        <FormItem label="Garantía desde" name="inicioGarantia">
                            <DatePickerItem placeholder="Fecha" style={{ width: "100%" }} format="DD/MM/YYYY" />
                        </FormItem>
                    </Col>
                    <Col xs={12}>
                        <FormItem label="Garantía hasta" name="finGarantia">
                            <DatePickerItem placeholder="Fecha" style={{ width: "100%" }} format="DD/MM/YYYY" />
                        </FormItem>
                    </Col>
                </Row>
            </DynamicCardForm>
        );
    }

    renderUbicacion() {
        if (this.props.modo === "editar") {
            return (
                <DynamicCardForm title="Cliente y ubicación" xs={24}>
                    <div className="cmms-banda" style={{ marginBottom: 0 }}>
                        <InfoCircleOutlined style={{ color: "#2fb8d6", marginTop: 2 }} />
                        <div>
                            <b>{this.state.clienteId ? this.state.ubicacionActualTexto || "Cliente asignado" : "Sin cliente"}</b>
                            <div className="cmms-banda-secundaria">
                                Para cambiar el cliente o la ubicación use <b>Transferir</b> en la ficha del activo: así queda el historial de dónde ha estado el equipo.
                            </div>
                        </div>
                    </div>
                </DynamicCardForm>
            );
        }
        const sinCliente = !this.state.clienteId;
        return (
            <DynamicCardForm title="Cliente y ubicación" xs={24}>
                <div className="cmms-ayuda">Opcional. Puede registrar el equipo sin cliente y asignarlo después con Transferir.</div>
                <Row gutter={12}>
                    <Col xs={24} lg={9}>
                        <FormItem label="Cliente" name="clienteId" tooltip="Dueño del equipo (hospital o clínica).">
                            <SelectClientesItem empresaId={this.props.empresaId} headersToken={this.props.headersToken} width="100%" placeholder="Buscar cliente" onChange={this.onChangeCliente} />
                        </FormItem>
                    </Col>
                    <Col xs={24} md={8} lg={5} className={sinCliente ? "cmms-campo-espera" : ""}>
                        <FormItem label="Sucursal" name="direccionId" tooltip="Sucursal o unidad médica del cliente (sus direcciones).">
                            <SelectItem placeholder={sinCliente ? "Elija un cliente" : "Sucursal"} allowClear disabled={sinCliente}>
                                {this.state.sucursales.map((s) => (
                                    <Option key={s._id} value={s._id}>
                                        {s.nombre}
                                    </Option>
                                ))}
                            </SelectItem>
                        </FormItem>
                    </Col>
                    <Col xs={24} md={8} lg={5} className={sinCliente ? "cmms-campo-espera" : ""}>
                        <FormItem label="Ubicación" name="ubicacion" max={80} tooltip="Dónde está el equipo (Quirófanos, Urgencias...). Escriba o elija una de las ya usadas en este cliente. Si se deja vacía queda como General.">
                            <CampoSugerenciasCMMS
                                empresaId={this.props.empresaId}
                                headersToken={this.props.headersToken}
                                placeholder={sinCliente ? "Elija un cliente" : "General"}
                                disabled={sinCliente}
                                params={{ campo: "ubicacion", clienteId: this.state.clienteId, direccionId: this.state.direccionId }}
                            />
                        </FormItem>
                    </Col>
                    <Col xs={24} md={8} lg={5} className={sinCliente ? "cmms-campo-espera" : ""}>
                        <FormItem label="Área" name="area" max={80} tooltip="Área dentro de la ubicación (Qx 1, Hab. 17...). Si se deja vacía queda como General.">
                            <CampoSugerenciasCMMS
                                empresaId={this.props.empresaId}
                                headersToken={this.props.headersToken}
                                placeholder={sinCliente ? "Elija un cliente" : "General"}
                                disabled={sinCliente}
                                params={{ campo: "area", clienteId: this.state.clienteId, ubicacion: this.state.ubicacion }}
                            />
                        </FormItem>
                    </Col>
                </Row>
            </DynamicCardForm>
        );
    }

    renderArchivos() {
        return (
            <DynamicCardForm title="Fotos y documentos" xs={24}>
                <Row gutter={14}>
                    <Col xs={24} md={12}>
                        <FormItem label="Fotos del equipo" name="fotos" valuePropName="fileList" getValueFromEvent={normFile} tooltip="La primera foto se muestra en la ficha del activo.">
                            <Upload listType="picture-card" multiple accept="image/*" beforeUpload={subirSinEnviar(5)}>
                                <div>
                                    <PictureOutlined />
                                    <div style={{ marginTop: 6, fontSize: 12 }}>Agregar</div>
                                </div>
                            </Upload>
                        </FormItem>
                    </Col>
                    <Col xs={24} md={12}>
                        <FormItem
                            label="Documentos y manuales"
                            name="archivos"
                            valuePropName="fileList"
                            getValueFromEvent={normFile}
                            tooltip="Manuales, diagramas, factura, carta de garantía... (PDF o imagen)."
                        >
                            <Upload.Dragger className="cmms-dragger" multiple accept=".pdf,image/*" beforeUpload={subirSinEnviar(20)}>
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text">Arrastre archivos aquí o haga clic</p>
                                <p className="ant-upload-hint">PDF o imagen, hasta 20MB</p>
                            </Upload.Dragger>
                        </FormItem>
                    </Col>
                    <Col xs={24}>
                        <FormItem label="Descripción u observaciones" name="descripcion">
                            <TextAreaItem rows={2} placeholder="Accesorios, condiciones, notas generales del equipo..." />
                        </FormItem>
                    </Col>
                </Row>
            </DynamicCardForm>
        );
    }

    render() {
        const editar = this.props.modo === "editar";
        return (
            <>
                <ModalFormCMMS
                    titulo={editar ? "Editar activo" : "Nuevo activo"}
                    subtitulo={editar ? "Equipo médico del cliente" : "Registre un equipo médico. El folio se asigna al guardar."}
                    icono={<IconActivos size={22} color="#2fb8d6" marginRight={0} />}
                    visible={this.props.visible}
                    loading={this.state.loading}
                    onCancel={() => this.props.onClose()}
                    formRef={this.formRef}
                    name="activoCMMS"
                    onFinish={this.onFinish}
                    onValuesChange={this.onValuesChange}
                    textoGuardar={editar ? "Guardar cambios" : "Registrar activo"}
                    width={1040}
                >
                    {this.renderEquipo()}
                    {this.renderEstado()}
                    {this.renderUbicacion()}
                    {this.renderArchivos()}
                </ModalFormCMMS>
                <ModalMarca
                    visible={this.state.modalMarca}
                    modo="agregar"
                    marcaId={null}
                    empresaId={this.props.empresaId}
                    usuarioId={this.props.usuarioId}
                    headersToken={this.props.headersToken}
                    onClose={() => this.setState((s) => ({ modalMarca: false, llaveMarcas: s.llaveMarcas + 1 }))}
                />
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
    usuarioId: state.user.usuarioId,
});

export default connect(mapStateToProps)(ModalActivo);
